"use client";

import { useAuth } from "@/hooks/use-auth";
import { getCountriesWithPhoneCodes } from "@/info/countries";
import { isPhoneNumberValid } from "@/info/login";
import { propertyTypeOptions } from "@/info/unit/property-types";
import { Filters } from "@/types/filters";
import { cn } from "@/utils/cn";
import { deepEqual as isEqualFunc } from "fast-equals";
import { useEffect, useMemo, useRef, useState } from "react";
import { Control, FormProvider, useForm, UseFormSetValue, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { Button } from "../rac/button";
import { Input } from "../rhf/input";
import { PropertyTypeField } from "../rhf/property-type-field";
import { ButtonActions } from "./action-buttons";
import { useFormContext } from "./context/FormProvider";
import { OTPModal } from "./otp-verify";
export interface ProfileFormData {
  name: string;
  phoneNumber: string;
  email: string;
  numUnits?: number;
  relationship: string;
  propertyType: {
    type: string;
    subType: string;
    houseType?: string;
  };
  isCommunity?: boolean;
  entityType?: string;
  companyName?: string;
  officeAddress?: string;
  gstNo?: string;
}
interface StepProfileProps {
  onNext: (data: ProfileFormData) => void;
  rawUnitId?: string | null;
  sellerUnitId?: string | null;
  urlGroupId?: string | null;
  groupId?: string | null;
  isLoading?: boolean;
}
const entityOptions = [{
  value: "groupOfIndividuals",
  label: "Group of Individuals"
}, {
  value: "proprietorship",
  label: "Proprietorship"
}, {
  value: "partnershipFirm",
  label: "Partnership Firm"
}, {
  value: "pvtLtdCompany",
  label: "Private Limited Company"
}, {
  value: "publicListedCompany",
  label: "Public Listed Company"
}, {
  value: "limitedLiabilityPartnership",
  label: "LLP - limited liability Partnership"
}];
export function StepProfile({
  onNext,
  rawUnitId,
  urlGroupId,
  isLoading
}: StepProfileProps) {
  const {
    user,
    isAuthenticated,
    sendOtp,
    login
  } = useAuth();
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [currentPhoneNumber, setCurrentPhoneNumber] = useState<string>("");
  const pendingFormData = useRef<ProfileFormData | null>(null);
  const {
    state,
    updateStepData
  } = useFormContext();

  // Define relationship options dynamically based on existing IDs
  const relationshipOptions = useMemo(() => {
    const hasAnyId = urlGroupId || rawUnitId;
    const isBLO = urlGroupId && !rawUnitId;
    return [{
      value: "owner",
      label: "Owner",
      disabled: hasAnyId && isBLO
    }, {
      value: "builder",
      label: "Builder/Developer",
      disabled: hasAnyId && !isBLO
    }, {
      value: "landOwner",
      label: "LandOwner",
      disabled: hasAnyId && !isBLO
    }, {
      value: "investor",
      label: "Investor",
      disabled: hasAnyId && !isBLO
    }, {
      value: "agent",
      label: "Agent",
      disabled: hasAnyId && isBLO
    }];
  }, [rawUnitId, urlGroupId]);
  const contextProfile = state.profile as Partial<ProfileFormData>;

  // Resolve relationship once during initialization only
  const getResolvedRelationship = (relationship?: string) => {
    const allowedRelationships = ["agent", "builder", "landOwner", "owner", "investor"];
    if (!relationship) return "";
    if (allowedRelationships.includes(relationship)) return relationship;
    return "";
  };

  // Capture default values only once on mount to prevent form resets
  // when contextProfile updates from user input
  const initialDefaultValues = useRef<ProfileFormData | null>(null);
  if (!initialDefaultValues.current) {
    initialDefaultValues.current = {
      name: contextProfile?.name || user?.name || "",
      phoneNumber: contextProfile?.phoneNumber || user?.phones?.[0]?.number || "",
      email: contextProfile?.email || user?.emails?.[0]?.address || "",
      relationship: getResolvedRelationship(contextProfile?.relationship),
      propertyType: {
        type: contextProfile?.propertyType?.type || "",
        subType: contextProfile?.propertyType?.subType || "",
        houseType: contextProfile?.propertyType?.houseType || ""
      },
      isCommunity: contextProfile?.isCommunity || false,
      entityType: contextProfile?.entityType || "",
      companyName: contextProfile?.companyName || "",
      officeAddress: contextProfile?.officeAddress || "",
      gstNo: contextProfile?.gstNo || ""
    };
  }
  const defaultValues = initialDefaultValues.current;
  const form = useForm<ProfileFormData>({
    defaultValues,
    mode: "onChange"
  });
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState,
    reset,
    ...useFormProps
  } = form;

  // Watch form values
  const watchedValues = useWatch({
    control
  }) as ProfileFormData;
  const countries = getCountriesWithPhoneCodes();

  // Memoize the watched values to prevent unnecessary re-renders
  const memoizedWatchedValues = useMemo(() => watchedValues, [watchedValues]);

  // Update context on form change with debounced updates
  const prevWatched = useRef(memoizedWatchedValues);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle immediate cleanup of irrelevant fields when relationship changes
  useEffect(() => {
    const newRelationship = memoizedWatchedValues.relationship;
    const oldRelationship = prevWatched.current.relationship;
    if (newRelationship && oldRelationship && newRelationship !== oldRelationship) {
      const isBusinessRole = ["builder", "landOwner", "investor"].includes(newRelationship);
      const isOwnerOrAgent = ["owner", "agent"].includes(newRelationship);
      if (isOwnerOrAgent) {
        // Clear business fields
        setValue("entityType", "", {
          shouldDirty: true
        });
        setValue("companyName", "", {
          shouldDirty: true
        });
        setValue("officeAddress", "", {
          shouldDirty: true
        });
        setValue("gstNo", "", {
          shouldDirty: true
        });
      } else if (isBusinessRole) {
        // Clear property type fields
        setValue("propertyType", {
          type: "",
          subType: "",
          houseType: ""
        }, {
          shouldDirty: true
        });
        setValue("isCommunity", false, {
          shouldDirty: true
        });
      }
    }
  }, [memoizedWatchedValues.relationship, setValue]);
  useEffect(() => {
    if (!isEqualFunc(prevWatched.current, memoizedWatchedValues)) {
      // Clear previous timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      // Debounce the update to prevent rapid changes
      updateTimeoutRef.current = setTimeout(() => {
        updateStepData("profile", memoizedWatchedValues);
        prevWatched.current = memoizedWatchedValues;
      }, 300);
    }

    // Cleanup timeout on unmount
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [memoizedWatchedValues, updateStepData]);

  // Handle sending OTP for phone verification (separate from form submission)
  const handleSendOtp = async () => {
    const phoneNumber = watchedValues.phoneNumber;
    if (!phoneNumber) {
      toast.error("Please enter a phone number first");
      return;
    }
    setIsSendingOtp(true);
    try {
      const phoneWithCountryCode = !phoneNumber.startsWith("+") && phoneNumber.length === 10 ? `+91${phoneNumber.replace(/[^\d]/g, "")}` : phoneNumber;
      const isIndianNumber = phoneNumber.startsWith("+91") || !phoneNumber.startsWith("+") && phoneNumber.length === 10;
      const otpMethod = isIndianNumber ? "sms" : "whatsapp";
      const success = await sendOtp(phoneWithCountryCode, false, otpMethod);
      if (success) {
        setCurrentPhoneNumber(phoneWithCountryCode);
        setShowOtpModal(true);
        pendingFormData.current = null;
        toast.success("OTP sent successfully");
      } else {
        throw new Error("Failed to send OTP");
      }
    } catch (err) {
      toast.error(`${err instanceof Error ? err.message : "Failed to send OTP"}, Please try again.`);
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Handle OTP verification for phone number verification
  const handleVerifyOtp = async (otp: string) => {
    if (!currentPhoneNumber) return;
    setIsVerifyingOtp(true);
    try {
      const loginResult = await login(currentPhoneNumber, otp);
      if (loginResult) {
        setShowOtpModal(false);
        setCurrentPhoneNumber("");
        toast.success("Phone number verified successfully");

        // If there's pending form data, proceed to next step
        if (pendingFormData.current) {
          onNext(pendingFormData.current);
          pendingFormData.current = null;
        }
      } else {
        throw new Error("Failed to verify OTP");
      }
    } catch (err_0) {
      toast.error(`${err_0 instanceof Error ? err_0.message : "Failed to verify OTP"}, Please try again.`);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // Handle OTP modal close
  const handleCloseOtpModal = () => {
    setShowOtpModal(false);
    setCurrentPhoneNumber("");
    setIsVerifyingOtp(false);
    pendingFormData.current = null; // Clear pending form data when modal is closed
  };

  // Submit
  const onSubmit = async (data: ProfileFormData) => {
    const country_0 = countries.find(country => data.phoneNumber?.startsWith(`${country.code}`));
    if (!country_0?.code) {
      toast.error("Please enter a phone number with country code");
      return;
    }
    if (!isPhoneNumberValid(data.phoneNumber, country_0?.code || "+91")) {
      toast.error("Please enter a valid phone number");
      return;
    }
    // If user is not authenticated, send OTP simutaneously
    if (!isAuthenticated) {
      try {
        const phoneWithCountryCode_0 = !data.phoneNumber.startsWith("+") && data.phoneNumber.length === 10 ? `+91${data.phoneNumber.replace(/[^\d]/g, "")}` : data.phoneNumber;
        const isIndianNumber_0 = data.phoneNumber.startsWith("+91") || !data.phoneNumber.startsWith("+") && data.phoneNumber.length === 10;
        const otpMethod_0 = isIndianNumber_0 ? "sms" : "whatsapp";
        const success_0 = await sendOtp(phoneWithCountryCode_0, false, otpMethod_0);
        if (success_0) {
          setCurrentPhoneNumber(phoneWithCountryCode_0);
          pendingFormData.current = data; // Store form data for after verification
          setShowOtpModal(true);
        } else {
          throw new Error("Failed to send OTP");
        }
      } catch (err_1) {
        toast.error(`${err_1 instanceof Error ? err_1.message : "Failed to send OTP"}, Please try again.`);
      }
    } else {
      onNext(data);
    }
  };
  const isPropertyCheckeNeeded = !["builder", "landOwner", "investor"].includes(watchedValues.relationship);
  const phoneVerified = user?.phones?.find(phone => phone.number?.replace(/^\+\d{1,2}/, "") === watchedValues.phoneNumber?.replace(/^\+\d{1,2}/, ""))?.verified || false;
  const emailVerified = user?.emails?.find(email => email.address === watchedValues.email)?.verified || false;
  const country_2 = countries.find(country_1 => watchedValues.phoneNumber?.startsWith(`${country_1.code}`));
  const {
    name,
    phoneNumber: phoneNumber_0,
    relationship: relationship_0,
    propertyType,
    entityType,
    companyName,
    officeAddress
  } = watchedValues;
  const isOwnerOrAgent_0 = ["owner", "agent"].includes(relationship_0);
  const isBusinessRole_0 = ["builder", "landOwner", "investor"].includes(relationship_0);
  const hasBasicInfo = !!(name && phoneNumber_0 && relationship_0);
  const hasPropertyType = !isPropertyCheckeNeeded || !!propertyType?.type;
  const hasRelationshipProperty = !isOwnerOrAgent_0 || !!propertyType?.type;
  const hasBusinessInfo = !isBusinessRole_0 || !!(entityType && companyName && officeAddress);
  const canProceed = relationship_0 && hasBasicInfo && hasPropertyType && hasRelationshipProperty && hasBusinessInfo && formState.isValid;
  return <>
			<FormProvider {...{
      control,
      handleSubmit,
      watch,
      setValue,
      formState,
      reset,
      ...useFormProps
    }}>
				<form onSubmit={handleSubmit(onSubmit)} className="h-full flex flex-col">
					<div className="flex-1 space-y-6 lg:space-y-8">
						<div className="space-y-3 lg:space-y-4">
							<h2 className="text-2xl lg:text-3xl font-bold text-neutral-900 leading-tight">{"Let's get started!"}</h2>
							<p className="text-neutral-600 text-base lg:text-lg leading-relaxed">
								This will only take a minute — about as long as your coffee break ☕
							</p>
							<h3 className="text-xl lg:text-2xl font-semibold text-neutral-900 mt-6 lg:mt-8">
								Tell us a little about yourself
							</h3>
						</div>

						<Input name="name" label="Name" placeholder="Enter name here" rules={{
            required: "Name is required"
          }} control={control} />
						<div className="space-y-2">
							<label className="text-sm font-medium text-gray-700">Mobile No</label>
							<div className="flex items-center gap-2">
								<div className="flex-1">
									<Input name="phoneNumber" control={control} rules={{
                  required: "Phone number is required"
                }} disabled={phoneVerified} />
								</div>
								{phoneVerified ? <span className="px-3 py-1 text-xs font-semibold text-green-800 bg-green-100 border border-green-200 rounded-full shadow-sm whitespace-nowrap">
										Verified
									</span> : <Button variant="outline" size="sm" onPress={handleSendOtp} isDisabled={isSendingOtp || !watchedValues?.phoneNumber?.startsWith("+") || !isPhoneNumberValid(watchedValues?.phoneNumber, country_2?.code || "+91")} className="whitespace-nowrap">
										{isSendingOtp ? "Sending..." : "Send OTP"}
									</Button>}
							</div>
							{!phoneVerified && <span className="text-xs text-red-500 mt-1">
									{!watchedValues?.phoneNumber?.startsWith("+") ? "Please enter a phone number with country code" : !isPhoneNumberValid(watchedValues?.phoneNumber, country_2?.code || "+91") ? "Please enter a valid phone number" : "Verify number by clicking Send OTP"}
								</span>}
						</div>
						<div className="space-y-2">
							<label className="text-sm font-medium text-gray-700">Email ID</label>
							<div className="flex items-center gap-2">
								<div className="flex-1">
									<Input name="email" type="email" control={control} placeholder="Enter email here" hideLabel />
								</div>
								{emailVerified && <span className="px-3 py-1 text-xs font-semibold text-green-800 bg-green-100 border border-green-200 rounded-full shadow-sm whitespace-nowrap">
										Verified
									</span>}
							</div>
						</div>
						<Input name="relationship" type="radio" itemsClassName="grid grid-cols-1 md:grid-cols-3" options={relationshipOptions} label="Relationship type ? *" control={control} rules={{
            required: "Relationship is required"
          }} />
						{["builder", "landOwner", "investor"].includes(watchedValues.relationship) && <>
								<Input name="entityType" type="radio" itemsClassName="grid grid-cols-1 md:grid-cols-2" options={entityOptions} label="Entity Type *" control={control} rules={{
              required: "Entity type is required"
            }} />
								<Input name="companyName" label="Name of the company *" placeholder="Enter company name" control={control} rules={{
              required: "Company name is required"
            }} />
								<Input name="officeAddress" label="Office Address *" placeholder="Enter office address" control={control} rules={{
              required: "Office address is required"
            }} />
								<Input name="gstNo" label="GST No *" placeholder="Enter GST number" className="mb-6" control={control} rules={{
              required: "GST number is required"
              // pattern: ["builder", "landOwner", "investor"].includes(watchedValues.relationship)
              // 	? {
              // 			value: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
              // 			message: "Please enter a valid GST number",
              // 		}
              // 	: undefined,
            }} />
							</>}
						{["owner", "agent"].includes(watchedValues.relationship) && <>
								<PropertyTypeField options={propertyTypeOptions} name="propertyType.type" exactPropertyTypeName="propertyType.subType" isCommunityFieldName="isCommunity" excludeOnly control={control as unknown as Control<Filters>} setValue={setValue as unknown as UseFormSetValue<Filters>} required />

								<Input className={cn("ml-2", watchedValues.propertyType?.type === "house" && ["h:p-", "h:i-"].includes(watchedValues.propertyType?.subType) ? "" : "hidden")} labelClassName="text-title-small" type="radio" label="What type is the house?" name="propertyType.houseType" options={[{
              value: "h:p:s",
              label: "Single Family"
            }, {
              value: "h:p:m",
              label: "Multi Family"
            }]} control={control} rules={{
              required: watchedValues.propertyType?.type === "house" && ["h:p-", "h:i-"].includes(watchedValues.propertyType?.subType) ? "Please select an option" : false
            }} />
							</>}
					</div>
					<ButtonActions next disabled={!canProceed} isLoading={isLoading} />
				</form>
			</FormProvider>
			<OTPModal isOpen={showOtpModal} onClose={handleCloseOtpModal} onVerify={handleVerifyOtp} phoneNumber={currentPhoneNumber || ""} isVerifying={isVerifyingOtp} />
		</>;
}